import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  FORMAT_TEXT_COMMAND
} from "lexical";
import { $createHeadingNode, $createQuoteNode, HeadingNode, QuoteNode, $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $createListItemNode, $createListNode, $isListNode, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode } from "@lexical/list";
import { $createLinkNode, $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $setBlocksType } from "@lexical/selection";
import {
  ArrowRight,
  Bold,
  CircleDot,
  Heading2,
  Italic,
  Heart,
  Instagram,
  Link as LinkIcon,
  List,
  LogOut,
  Menu,
  Mic,
  Music2,
  Pause,
  Play,
  Plus,
  Quote,
  Save,
  Search,
  Sparkles,
  Upload,
  Users,
  Video,
  Wand2,
  X
} from "lucide-react";
import "./styles.css";
import {
  LANDING_TEXT_DEFAULTS,
  LANDING_TEXT_LIMITS
} from "../shared/landingContent.js";
import { FEATURED_STORIES } from "../shared/featuredStories.js";
import { VISUAL_GALLERY_DEFAULTS } from "../shared/visualGallery.js";

const FALLBACK_COVERS = FEATURED_STORIES.map((story) => story.coverUrl);
// Keep the saved edition content available for a future relaunch.
const SHOW_EDITIONS = false;
const SiteTextContext = createContext(null);

const api = {
  async request(path, options = {}) {
    const response = await fetch(path, {
      credentials: "include",
      headers: options.body instanceof FormData ? undefined : { "Content-Type": "application/json" },
      ...options
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || "Zahteva ni uspela");
    return data;
  },
  get(path) {
    return this.request(path);
  },
  post(path, body) {
    return this.request(path, {
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body)
    });
  },
  put(path, body) {
    return this.request(path, { method: "PUT", body: JSON.stringify(body) });
  },
  patch(path, body) {
    return this.request(path, { method: "PATCH", body: JSON.stringify(body) });
  },
  delete(path) {
    return this.request(path, { method: "DELETE" });
  }
};

const emptyPost = {
  id: null,
  title: "",
  slug: "",
  excerpt: "",
  status: "draft",
  coverMediaId: null,
  coverUrl: "",
  content: { blocks: [] }
};

const FORMAT_TOOLS = [
  { kind: "heading", label: "Podnaslov", icon: Heading2 },
  { kind: "bold", label: "Krepko", icon: Bold },
  { kind: "italic", label: "Ležeče", icon: Italic },
  { kind: "quote", label: "Citat", icon: Quote },
  { kind: "list", label: "Seznam", icon: List },
  { kind: "link", label: "Povezava", icon: LinkIcon }
];

function bodyTextToBlocks(bodyText) {
  return bodyText
    .split(/\n{2,}/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => ({ type: "paragraph", text }));
}

function createInlineLexicalNodes(text) {
  return parseInlineText(text).flatMap((part) => {
    if (typeof part === "string") return [$createTextNode(part)];

    if (part.type === "link") {
      const link = $createLinkNode(part.href);
      link.append($createTextNode(part.text));
      return [link];
    }

    const textNode = $createTextNode(part.text);
    if (part.type === "strong") textNode.toggleFormat("bold");
    if (part.type === "em") textNode.toggleFormat("italic");
    return [textNode];
  });
}

function appendInlineLexicalText(parent, text) {
  createInlineLexicalNodes(text).forEach((node) => parent.append(node));
}

function importBodyTextToLexical(bodyText) {
  const root = $getRoot();
  root.clear();
  const blocks = bodyText.split(/\n{2,}/).map((text) => text.trim()).filter(Boolean);

  if (!blocks.length) {
    root.append($createParagraphNode());
    return;
  }

  blocks.forEach((block) => {
    if (block.startsWith("## ")) {
      const heading = $createHeadingNode("h2");
      appendInlineLexicalText(heading, block.slice(3));
      root.append(heading);
      return;
    }

    if (block.startsWith("> ")) {
      const quote = $createQuoteNode();
      appendInlineLexicalText(quote, block.replace(/^> ?/gm, ""));
      root.append(quote);
      return;
    }

    const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
    if (lines.length > 0 && lines.every((line) => line.startsWith("- "))) {
      const list = $createListNode("bullet");
      lines.forEach((line) => {
        const item = $createListItemNode();
        appendInlineLexicalText(item, line.slice(2));
        list.append(item);
      });
      root.append(list);
      return;
    }

    const paragraph = $createParagraphNode();
    appendInlineLexicalText(paragraph, block);
    root.append(paragraph);
  });
}

function serializeLexicalInlineNode(node) {
  if ($isTextNode(node)) {
    let text = node.getTextContent();
    if (node.hasFormat("bold")) text = `**${text}**`;
    if (node.hasFormat("italic")) text = `_${text}_`;
    return text;
  }

  if ($isLinkNode(node)) {
    return `[${node.getTextContent()}](${node.getURL()})`;
  }

  if ($isElementNode(node)) {
    return node.getChildren().map(serializeLexicalInlineNode).join("");
  }

  return node.getTextContent();
}

function serializeLexicalElement(node) {
  if ($isHeadingNode(node)) return `## ${node.getChildren().map(serializeLexicalInlineNode).join("")}`;
  if ($isQuoteNode(node)) return `> ${node.getChildren().map(serializeLexicalInlineNode).join("").replace(/\n/g, "\n> ")}`;

  if ($isListNode(node)) {
    return node.getChildren()
      .map((item) => `- ${item.getChildren().map(serializeLexicalInlineNode).join("")}`)
      .join("\n");
  }

  return node.getChildren().map(serializeLexicalInlineNode).join("");
}

function serializeLexicalToBodyText() {
  return $getRoot()
    .getChildren()
    .map(serializeLexicalElement)
    .map((text) => text.trim())
    .filter(Boolean)
    .join("\n\n");
}

function useRoute() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (nextPath) => {
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { path, navigate };
}

function App() {
  const { path, navigate } = useRoute();
  const [mobileOpen, setMobileOpen] = useState(false);
  const editor = useSiteTextEditor(path);

  const page = useMemo(() => {
    if (path.startsWith("/admin")) return <AdminPage navigate={navigate} />;
    if (path.startsWith("/journal/")) return <ArticlePage slug={decodeURIComponent(path.replace("/journal/", ""))} navigate={navigate} />;
    if (path === "/journal") return <BlogPage navigate={navigate} />;
    return <HomePage navigate={navigate} />;
  }, [path, navigate]);

  return (
    <SiteTextContext.Provider value={editor}>
    <div className={`app-shell${editor.editing ? " landing-editing" : ""}`}>
      <a className="skip-link" href="#main-content">{editor.editable("skipLink")}</a>
      <SiteHeader navigate={navigate} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} solid={path !== "/"} />
      {page}
      {!path.startsWith("/admin") && editor.adminUser && (
        <LandingEditorToolbar
          editing={editor.editing}
          dirty={editor.dirty}
          busy={editor.saving}
          status={editor.editorStatus}
          error={editor.editorError}
          onEdit={editor.startEditing}
          onCancel={editor.cancelEditing}
          onSave={editor.saveLandingContent}
          navigate={(nextPath) => {
            if (editor.dirty && !window.confirm("Zavrnem neshranjene spremembe in odprem admin?")) return;
            editor.discardEditing();
            navigate(nextPath);
          }}
        />
      )}
    </div>
    </SiteTextContext.Provider>
  );
}

function SiteHeader({ navigate, mobileOpen, setMobileOpen, solid }) {
  const { editable, blockNavigationWhileEditing } = useContext(SiteTextContext);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 36);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["navStories", "/journal"],
    ["navGallery", "/#gallery"],
    ["navAttitude", "/#attitude"],
    ...(SHOW_EDITIONS ? [["navEditions", "/#editions"]] : []),
    ["navContact", "/#contact"]
  ];

  return (
    <header className={scrolled || solid ? "site-header scrolled" : "site-header"}>
      <button className="brand" onClick={() => navigate("/")} aria-label="Pojdi domov">
        <img src="/assets/lola-absolut-logo.png" alt="Lola Absolut" />
      </button>
      <nav className={mobileOpen ? "nav-links open" : "nav-links"}>
        {links.map(([label, href]) => (
          <button
            key={href}
            onClick={(event) => {
              if (blockNavigationWhileEditing(event)) return;
              setMobileOpen(false);
              if (href.includes("#")) {
                navigate("/");
                setTimeout(() => document.querySelector(href.slice(href.indexOf("#")))?.scrollIntoView({ behavior: "smooth" }), 40);
              } else {
                navigate(href);
              }
            }}
          >
            {editable(label)}
          </button>
        ))}
      </nav>
      <div className="header-actions">
        <button className="header-cta" onClick={(event) => {
          if (!blockNavigationWhileEditing(event)) navigate("/journal");
        }}>
          {editable("navCta")} <ArrowRight size={15} />
        </button>
        <button className="icon-button menu-button" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meni">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
    </header>
  );
}

function EditableText({
  as: Tag = "span",
  field,
  value,
  editing,
  multiline = false,
  onChange,
  className = ""
}) {
  const element = useRef(null);
  // Let the browser own text while typing so React does not reset the caret.
  useLayoutEffect(() => {
    if (element.current && element.current.textContent !== value) {
      element.current.textContent = value || "";
    }
  }, [value]);

  function handlePaste(event) {
    if (!editing) return;
    event.preventDefault();
    const pasted = event.clipboardData.getData("text/plain");
    const plainText = multiline ? pasted : pasted.replace(/\s+/g, " ");
    document.execCommand("insertText", false, plainText);
  }

  return (
    <Tag
      ref={element}
      className={`${className} inline-editable${editing ? " is-editing" : ""}`.trim()}
      contentEditable={editing}
      suppressContentEditableWarning
      data-inline-field={field}
      spellCheck={editing}
      title={editing ? "Klikni in uredi besedilo" : undefined}
      onClick={(event) => {
        if (editing) event.stopPropagation();
      }}
      onInput={(event) => {
        const limit = LANDING_TEXT_LIMITS[field];
        const nextValue = (event.currentTarget.textContent || "").slice(0, limit);
        onChange(field, nextValue);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        if (multiline) document.execCommand("insertText", false, "\n");
      }}
      onPaste={handlePaste}
    />
  );
}

function LandingEditorToolbar({ editing, dirty, busy, status, error, onEdit, onCancel, onSave, navigate }) {
  return (
    <aside className="landing-editor-toolbar" aria-label="Urejanje besedila strani">
      <div className="landing-editor-label">
        <Wand2 size={17} />
        <span>{editing ? "Urejanje v živo" : "Urejanje strani"}</span>
      </div>
      <div className="landing-editor-actions">
        <button type="button" onClick={() => navigate("/admin")} className="landing-editor-secondary">Admin</button>
        {editing ? (
          <>
            <button type="button" onClick={onCancel} className="landing-editor-secondary" disabled={busy}>Prekliči</button>
            <button type="button" onClick={onSave} className="landing-editor-save" disabled={!dirty || busy}>
              <Save size={16} /> {busy ? "Shranjujem…" : "Shrani spremembe"}
            </button>
          </>
        ) : (
          <button type="button" onClick={onEdit} className="landing-editor-save">
            <Wand2 size={16} /> Uredi besedila
          </button>
        )}
      </div>
      <span className={`landing-editor-message${error ? " error" : ""}`} aria-live="polite">
        {error || status}
      </span>
    </aside>
  );
}

function GalleryFigure({ item, className }) {
  return (
    <figure className={className}>
      <img src={item.imageUrl} alt={item.altText} loading="lazy" />
      <figcaption>{item.caption}</figcaption>
    </figure>
  );
}

function useSiteTextEditor(path) {
  const [adminUser, setAdminUser] = useState(null);
  const [savedContent, setSavedContent] = useState(LANDING_TEXT_DEFAULTS);
  const [draftContent, setDraftContent] = useState(LANDING_TEXT_DEFAULTS);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorStatus, setEditorStatus] = useState("");
  const [editorError, setEditorError] = useState("");
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    api.get("/api/page-content")
      .then((data) => {
        const nextContent = { ...LANDING_TEXT_DEFAULTS, ...(data.content || {}) };
        setSavedContent(nextContent);
        setDraftContent(nextContent);
        setContentLoaded(true);
      })
      .catch(() => setEditorError("Besedil ni mogoče naložiti. Osveži stran pred urejanjem."));
  }, []);

  useEffect(() => {
    let active = true;
    api.get("/api/auth/me").then((data) => {
      if (active) setAdminUser(data.user || null);
    }).catch(() => { if (active) setAdminUser(null); });
    return () => { active = false; };
  }, [path]);

  const pageContent = editing ? draftContent : savedContent;
  const dirty = JSON.stringify(draftContent) !== JSON.stringify(savedContent);

  useEffect(() => {
    if (!editing || !dirty) return undefined;
    const confirmLeave = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", confirmLeave);
    return () => window.removeEventListener("beforeunload", confirmLeave);
  }, [editing, dirty]);

  function updateField(field, value) {
    setDraftContent((current) => ({ ...current, [field]: value }));
    setEditorStatus("");
    setEditorError("");
  }

  function startEditing() {
    if (!adminUser || !contentLoaded) return;
    setDraftContent(savedContent);
    setEditing(true);
    setEditorStatus("Klikni katerokoli označeno besedilo in ga spremeni neposredno na strani.");
    setEditorError("");
  }

  function cancelEditing() {
    if (dirty && !window.confirm("Zavrnem neshranjene spremembe?")) return;
    discardEditing();
  }

  function discardEditing() {
    setDraftContent(savedContent);
    setEditing(false);
    setEditorStatus("");
    setEditorError("");
  }

  async function saveLandingContent() {
    if (!adminUser || !contentLoaded || saving) return;
    setSaving(true);
    setEditorError("");
    setEditorStatus("");
    try {
      const changes = Object.fromEntries(Object.entries(draftContent).filter(([key, value]) => value !== savedContent[key]));
      const data = await api.put("/api/admin/page-content", { content: changes });
      const nextContent = { ...LANDING_TEXT_DEFAULTS, ...(data.content || {}) };
      setSavedContent(nextContent);
      setDraftContent(nextContent);
      setEditing(false);
      setEditorStatus("Spremembe so shranjene in vidne vsem obiskovalcem.");
    } catch (error) {
      setEditorError(error.message);
    } finally {
      setSaving(false);
    }
  }

  const editable = (field, props = {}) => (
    <EditableText
      field={field}
      value={pageContent[field]}
      editing={editing && !!adminUser && !saving && !path.startsWith("/admin")}
      onChange={updateField}
      {...props}
    />
  );

  const blockNavigationWhileEditing = (event) => {
    if (!editing || !adminUser || path.startsWith("/admin")) return false;
    event.preventDefault();
    return true;
  };

  return {
    editable, editing: editing && !!adminUser && !path.startsWith("/admin"),
    content: pageContent, blockNavigationWhileEditing, adminUser, dirty, saving,
    editorStatus, editorError, startEditing, cancelEditing, discardEditing, saveLandingContent
  };
}

function HomePage({ navigate }) {
  const [posts, setPosts] = useState([]);
  const [galleryItems, setGalleryItems] = useState(VISUAL_GALLERY_DEFAULTS);
  const { editable, editing, blockNavigationWhileEditing } = useContext(SiteTextContext);
  useEffect(() => {
    api.get("/api/posts").then((data) => setPosts(data.posts || [])).catch(console.error);
    api.get("/api/gallery").then((data) => {
      if (data.items?.length) setGalleryItems(data.items);
    }).catch(console.error);
  }, []);
  const galleryBySlot = Object.fromEntries(galleryItems.map((item) => [item.slot, item]));

  const attitudeItems = [
    ["01", "attitudeOneTitle", "attitudeOneCopy"],
    ["02", "attitudeTwoTitle", "attitudeTwoCopy"],
    ["03", "attitudeThreeTitle", "attitudeThreeCopy"]
  ];

  const editions = [
    ["/assets/lolaabsolut/editorial-night-standing-portrait.webp", "editionOneLabel", "editionOneTitle", "editionOneCopy"],
    ["/assets/lolaabsolut/editorial-night-seated-portrait.webp", "editionTwoLabel", "editionTwoTitle", "editionTwoCopy"],
    ["/assets/lolaabsolut/editorial-lake-portrait.webp", "editionThreeLabel", "editionThreeTitle", "editionThreeCopy"]
  ];

  return (
    <main className={`home-page${editing ? " landing-editing" : ""}`} id="main-content">
      <section className="hero">
        <img src="/assets/lolaabsolut/editorial-pool-hero-v2.webp" alt="Lola ob bazenu v živahnem pop-art slogu" className="hero-art hero-art-pool" />
        <div className="hero-shade" />
        <div className="hero-halftone" aria-hidden="true" />
        <div className="hero-content">
          {editable("heroEyebrow", { as: "p", className: "eyebrow" })}
          <h1>{editable("heroTitleTop")}<br /><span>{editable("heroTitleBottom")}</span></h1>
          {editable("heroCopy", { as: "p", className: "hero-copy" })}
          <div className="hero-actions">
            <button className="primary-button" onClick={(event) => {
              if (!blockNavigationWhileEditing(event)) navigate("/journal");
            }}>{editable("heroPrimaryCta")} <ArrowRight size={18} /></button>
            {SHOW_EDITIONS && <button className="hero-link" onClick={(event) => {
              if (!blockNavigationWhileEditing(event)) document.querySelector("#editions")?.scrollIntoView({ behavior: "smooth" });
            }}>{editable("heroSecondaryCta")}</button>}
          </div>
        </div>
        <img className="hero-brandmark" src="/assets/lola-absolut-logo.png" alt="" aria-hidden="true" />
        {editable("heroCaption", { as: "span", className: "hero-caption" })}
        <span className="scroll-cue">{editable("heroScroll")} <i /></span>
      </section>

      <div className="editorial-marquee" aria-label="O znamki">
        <div>{editable("marquee")}</div>
      </div>

      <section className="manifesto-section" id="manifesto">
        <div className="manifesto-image-wrap reveal-card">
          <img src="/assets/lolaabsolut/manifesto-mirror-portrait.jpg" alt="Lola pred ogledalom v živahnem pop-art slogu" width="1122" height="1402" loading="lazy" />
          {editable("manifestoImageLabel", { as: "span", className: "image-label" })}
        </div>
        <div className="manifesto-copy">
          <span className="section-kicker"><Sparkles size={15} /> {editable("manifestoKicker")}</span>
          <h2>{editable("manifestoTitleTop")}<br /><em>{editable("manifestoTitleBottom")}</em></h2>
          {editable("manifestoCopy", { as: "p" })}
          <button className="text-link" onClick={(event) => {
            if (!blockNavigationWhileEditing(event)) navigate("/journal");
          }}>{editable("manifestoCta")} <ArrowRight size={18} /></button>
        </div>
        <div className="manifesto-number" aria-hidden="true">01</div>
      </section>

      <section className="section latest-section" id="journal-preview">
        <div className="section-heading">
          <div>
            <span className="section-kicker"><CircleDot size={14} /> {editable("storiesKicker")}</span>
            <h2>{editable("storiesTitleTop")}<br />{editable("storiesTitleBottom")}</h2>
          </div>
          <button className="text-link" onClick={(event) => {
            if (!blockNavigationWhileEditing(event)) navigate("/journal");
          }}>
            {editable("storiesCta")} <ArrowRight size={17} />
          </button>
        </div>
        <div className="post-grid">
          {posts.slice(0, 4).map((post, index) => (
            <PostCard key={post.id} post={post} index={index} navigate={navigate} />
          ))}
        </div>
      </section>

      <section className="experiences-section" id="gallery">
        <div className="experiences-heading">
          <span className="section-kicker light"><Sparkles size={15} /> {editable("galleryKicker")}</span>
          <h2>{editable("galleryTitleTop")}<br /><span>{editable("galleryTitleBottom")}</span></h2>
          {editable("galleryCopy", { as: "p" })}
        </div>
        <div className="experience-collage">
          <GalleryFigure item={galleryBySlot.main || VISUAL_GALLERY_DEFAULTS[0]} className="experience-main" />
          <GalleryFigure item={galleryBySlot.top || VISUAL_GALLERY_DEFAULTS[1]} className="experience-small top" />
          <GalleryFigure item={galleryBySlot.bottom || VISUAL_GALLERY_DEFAULTS[2]} className="experience-small bottom" />
        </div>
      </section>

      <section className="attitude-section" id="attitude">
        <div className="attitude-title">
          {editable("attitudeKicker", { as: "span", className: "section-kicker light" })}
          <h2>{editable("attitudeTitleTop")}<br /><span>{editable("attitudeTitleBottom")}</span></h2>
        </div>
        <div className="attitude-list">
          {attitudeItems.map(([number, titleField, copyField]) => (
            <article className="attitude-item" key={number}>
              <span>{number}</span><div>{editable(titleField, { as: "h3" })}{editable(copyField, { as: "p" })}</div>
            </article>
          ))}
        </div>
      </section>

      {SHOW_EDITIONS && <section className="editions-section" id="editions">
        <div className="editions-heading">
          {editable("editionsKicker", { as: "span", className: "section-kicker" })}
          <h2>{editable("editionsTitleTop")}<br /><span>{editable("editionsTitleBottom")}</span></h2>
        </div>
        <div className="edition-grid">
          {editions.map(([image, labelField, titleField, copyField]) => (
            <article className="edition-card" key={image}>
              <div className="edition-image"><img src={image} alt="" loading="lazy" /></div>
              {editable(labelField, { as: "span" })}
              {editable(titleField, { as: "h3" })}
              {editable(copyField, { as: "p" })}
              <button className="text-link" onClick={blockNavigationWhileEditing}>{editable("editionCta")} <ArrowRight size={17} /></button>
            </article>
          ))}
        </div>
      </section>}

      <section className="quote-section">
        <span className="quote-mark">“</span>
        <blockquote>{editable("quoteTitleTop")}<br />{editable("quoteTitleBottom")}</blockquote>
        <img src="/assets/lola-absolut-logo.png" alt="Lola Absolut" />
      </section>

      <NewsletterForm editable={editable} editing={editing} />

      <Footer editable={editable} />

    </main>
  );
}

function BlogPage({ navigate }) {
  const { editable, editing, content } = useContext(SiteTextContext);
  const [posts, setPosts] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/api/posts").then((data) => setPosts(data.posts || [])).catch(console.error);
  }, []);

  const filtered = posts.filter((post) => `${post.title} ${post.excerpt}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <main className="page" id="main-content">
      <section className="page-hero journal-hero">
        {editable("journalTitle", { as: "h1" })}
        {editable("journalCopy", { as: "p" })}
        <label className="search-field">
          <Search size={18} />
          {editing ? editable("journalSearchPlaceholder", { className: "search-placeholder-editor" }) : (
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={content.journalSearchPlaceholder} aria-label={content.journalSearchPlaceholder} />
          )}
        </label>
      </section>
      <section className="section">
        <div className="post-grid wide">
          {filtered.map((post, index) => (
            <PostCard key={post.id} post={post} index={index} navigate={navigate} />
          ))}
        </div>
      </section>
      <Footer />
    </main>
  );
}

function ArticlePage({ slug, navigate }) {
  const { editable, blockNavigationWhileEditing } = useContext(SiteTextContext);
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/api/posts/${slug}`).then((data) => setPost(data.post)).catch((err) => setError(err.message));
  }, [slug]);

  if (error) {
    return (
      <main className="page">
        <section className="page-hero">
          {editable("articleNotFoundTitle", { as: "h1" })}
          <p>{error}</p>
          <button className="primary-button" onClick={(event) => {
            if (!blockNavigationWhileEditing(event)) navigate("/journal");
          }}>{editable("articleBack")}</button>
        </section>
      </main>
    );
  }

  if (!post) return <main className="page"><div className="loading">{editable("articleLoading")}</div></main>;

  return (
    <main className="page" id="main-content">
      <article className="article">
        <button className="text-link back-link" onClick={(event) => {
          if (!blockNavigationWhileEditing(event)) navigate("/journal");
        }}>
          <ArrowRight size={16} className="rotate" /> {editable("articleBack")}
        </button>
        <div className="article-header">
          <span>{formatDate(post.publishedAt || post.createdAt)}</span>
          <h1>{post.title}</h1>
          <p>{post.excerpt}</p>
        </div>
        <img className="article-logo" src="/assets/lola-absolut-logo.png" alt="Lola Absolut" width="484" height="450" />
        <div className="article-body">
          {post.content.blocks.map((block, index) => (
            <ContentBlock key={`${block.type}-${index}`} block={block} />
          ))}
        </div>
      </article>
      <Footer />
    </main>
  );
}

function AdminPage({ navigate }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/auth/me")
      .then((data) => setUser(data.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <main className="page"><div className="loading">Preverjam dostop...</div></main>;
  if (!user) return <LoginPanel onLogin={setUser} />;
  return <AdminDashboard user={user} setUser={setUser} navigate={navigate} />;
}

function LoginPanel({ onLogin }) {
  const [email, setEmail] = useState("admin@lolaabsolut.local");
  const [password, setPassword] = useState("change-me-now");
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    setError("");
    try {
      const data = await api.post("/api/auth/login", { email, password });
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <main className="page admin-page">
      <form className="login-panel glass-panel" onSubmit={submit}>
        <Music2 size={32} />
        <h1>lolaabsolut Uredništvo</h1>
        <p>Prijavi se za pisanje, nalaganje medijev in urejanje dnevnika.</p>
        {error && <div className="notice error">{error}</div>}
        <label>E-pošta<input value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Geslo<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <button className="primary-button">Vstopi v studio</button>
      </form>
    </main>
  );
}

function RichTextEditor({ value, onChange }) {
  const initialConfig = useMemo(() => ({
    namespace: "lolaabsolut-editor",
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    onError(error) {
      throw error;
    },
    editorState: () => importBodyTextToLexical(value)
  }), [value]);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="lexical-shell">
        <LexicalToolbar />
        <div className="lexical-editor-wrap">
          <RichTextPlugin
            contentEditable={<ContentEditable className="lexical-editor" aria-label="Vsebina objave" />}
            placeholder={<div className="lexical-placeholder">Tukaj piši objavo.</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <OnChangePlugin onChange={(editorState) => {
        editorState.read(() => onChange(serializeLexicalToBodyText()));
      }} />
    </LexicalComposer>
  );
}

function LexicalToolbar() {
  const [editor] = useLexicalComposerContext();

  function applyTool(kind) {
    if (kind === "bold") {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
      return;
    }

    if (kind === "italic") {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
      return;
    }

    if (kind === "list") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      return;
    }

    if (kind === "link") {
      const url = window.prompt("URL povezave", "https://");
      if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      return;
    }

    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (kind === "heading") {
        $setBlocksType(selection, () => $createHeadingNode("h2"));
      }

      if (kind === "quote") {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  }

  return (
    <div className="format-toolbar lexical-toolbar" aria-label="Oblikovanje vsebine">
      {FORMAT_TOOLS.map((tool) => {
        const Icon = tool.icon;
        return (
          <button key={tool.kind} type="button" onClick={() => applyTool(tool.kind)} title={tool.label}>
            <Icon size={16} />
            <span>{tool.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function AdminDashboard({ user, setUser, navigate }) {
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [galleryItems, setGalleryItems] = useState(VISUAL_GALLERY_DEFAULTS);
  const [adminSection, setAdminSection] = useState("posts");
  const [current, setCurrent] = useState(emptyPost);
  const [bodyText, setBodyText] = useState("");
  const [mediaBlocks, setMediaBlocks] = useState([]);
  const [editorRevision, setEditorRevision] = useState(0);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const loadAdminData = async () => {
    const [postData, userData, galleryData] = await Promise.all([
      api.get("/api/posts?admin=1"),
      api.get("/api/admin/users"),
      api.get("/api/gallery")
    ]);
    setPosts(postData.posts || []);
    setUsers(userData.users || []);
    setGalleryItems(galleryData.items || VISUAL_GALLERY_DEFAULTS);
  };

  useEffect(() => {
    loadAdminData().catch((err) => setError(err.message));
  }, []);

  function editPost(post) {
    setAdminSection("posts");
    setCurrent(post);
    setBodyText((post.content?.blocks || []).filter((block) => block.type === "paragraph").map((block) => block.text).join("\n\n"));
    setMediaBlocks((post.content?.blocks || []).filter((block) => block.type === "image" || block.type === "video"));
    setEditorRevision((value) => value + 1);
    setNotice("");
    setError("");
  }

  function newPost() {
    setAdminSection("posts");
    setCurrent(emptyPost);
    setBodyText("");
    setMediaBlocks([]);
    setEditorRevision((value) => value + 1);
    setNotice("");
    setError("");
  }

  async function savePost(event) {
    event.preventDefault();
    setNotice("");
    setError("");
    const content = {
      blocks: [
        ...bodyTextToBlocks(bodyText),
        ...mediaBlocks
      ]
    };
    const payload = { ...current, content };

    try {
      const data = current.id
        ? await api.put(`/api/admin/posts/${current.id}`, payload)
        : await api.post("/api/admin/posts", payload);
      setCurrent(data.post);
      setNotice("Objava shranjena");
      await loadAdminData();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deletePost() {
    if (!current.id || !window.confirm("Izbrišem to objavo?")) return;
    await api.delete(`/api/admin/posts/${current.id}`);
    newPost();
    await loadAdminData();
  }

  async function logout() {
    await api.post("/api/auth/logout", {});
    setUser(null);
  }

  return (
    <main className="page admin-page">
      <section className="admin-shell">
        <aside className="admin-sidebar glass-panel">
          <div>
            <span className="section-kicker"><CircleDot size={14} /> Studio</span>
            <h1>Uredništvo</h1>
            <p>{user.name}<br />{user.email}</p>
          </div>
          <button className="primary-button" onClick={newPost}><Plus size={17} /> Nova objava</button>
          <button
            className={adminSection === "gallery" ? "outline-button admin-nav-active" : "outline-button"}
            onClick={() => {
              setAdminSection("gallery");
              setNotice("");
              setError("");
            }}
          >
            <Sparkles size={17} /> Vizualna galerija
          </button>
          <button className="outline-button" onClick={() => navigate("/")}><ArrowRight size={16} /> Poglej stran</button>
          <button className="ghost-button" onClick={logout}><LogOut size={17} /> Odjava</button>
          {adminSection === "posts" && <div className="post-list">
            {posts.map((post) => (
              <button key={post.id} className={current.id === post.id ? "active" : ""} onClick={() => editPost(post)}>
                <span>{post.title}</span>
                <small>{statusLabel(post.status)}</small>
              </button>
            ))}
          </div>}
        </aside>

        {adminSection === "posts" ? <section className="admin-main">
          <form className="editor glass-panel" onSubmit={savePost}>
            <div className="editor-top">
              <div>
                <span className="section-kicker"><Wand2 size={14} /> Urejevalnik bloga</span>
                <h2>{current.id ? "Uredi objavo" : "Ustvari objavo"}</h2>
              </div>
              <div className="editor-actions">
                {current.id && <button type="button" className="ghost-button danger" onClick={deletePost}>Izbriši</button>}
                <button className="primary-button"><Save size={17} /> Shrani</button>
              </div>
            </div>
            {notice && <div className="notice success">{notice}</div>}
            {error && <div className="notice error">{error}</div>}
            <div className="form-grid">
              <label>Naslov<input value={current.title} onChange={(event) => setCurrent({ ...current, title: event.target.value })} /></label>
              <label>URL oznaka<input value={current.slug} onChange={(event) => setCurrent({ ...current, slug: event.target.value })} placeholder="samodejno-iz-naslova" /></label>
            </div>
            <label>Povzetek<textarea rows="3" value={current.excerpt} onChange={(event) => setCurrent({ ...current, excerpt: event.target.value })} /></label>
            <div className="form-grid">
              <label>Status
                <select value={current.status} onChange={(event) => setCurrent({ ...current, status: event.target.value })}>
                  <option value="draft">Osnutek</option>
                  <option value="published">Objavljeno</option>
                </select>
              </label>
              <label>Datum objave
                <input
                  type="date"
                  min="0001-01-01"
                  max="9999-12-31"
                  value={current.publicationDate ?? dateInputValue(current.publishedAt)}
                  onChange={(event) => setCurrent({ ...current, publicationDate: event.target.value })}
                  aria-label="Datum objave"
                  aria-describedby="publication-date-help"
                />
                <small id="publication-date-help">Datum določa prikaz in vrstni red objav, ne načrtuje objave. Prazno ohrani obstoječi datum oziroma uporabi datum prve objave.</small>
              </label>
            </div>
            <MediaUploader
              label="Naslovna slika"
              accept="image/*"
              onUploaded={(media) => setCurrent({ ...current, coverMediaId: media.id, coverUrl: media.urlPath })}
            />
            {current.coverUrl && <img className="cover-preview" src={current.coverUrl} alt="" />}
            <div className="content-editor">
              <label>Vsebina</label>
              <RichTextEditor
                key={`${current.id || "new"}-${editorRevision}`}
                value={bodyText}
                onChange={setBodyText}
              />
            </div>
            <TranscriptionBox onText={(text) => {
              setBodyText((value) => `${value}${value ? "\n\n" : ""}${text}`);
              setEditorRevision((value) => value + 1);
            }} />
            <div className="media-tools">
              <MediaUploader label="Dodaj sliko" accept="image/*" onUploaded={(media) => setMediaBlocks([...mediaBlocks, { type: "image", mediaId: media.id, url: media.urlPath, caption: "" }])} />
              <MediaUploader label="Dodaj video" accept="video/*" onUploaded={(media) => setMediaBlocks([...mediaBlocks, { type: "video", mediaId: media.id, url: media.urlPath, caption: "" }])} />
            </div>
            {mediaBlocks.length > 0 && (
              <div className="block-list">
                {mediaBlocks.map((block, index) => (
                  <div className="block-row" key={`${block.url}-${index}`}>
                    {block.type === "image" && <img src={block.url} alt="" />}
                    {block.type === "video" && <video src={block.url} controls />}
                    <input value={block.caption} onChange={(event) => {
                      const next = [...mediaBlocks];
                      next[index] = { ...next[index], caption: event.target.value };
                      setMediaBlocks(next);
                    }} placeholder="Napis" />
                    <button type="button" className="icon-button" onClick={() => setMediaBlocks(mediaBlocks.filter((_, itemIndex) => itemIndex !== index))}><X size={18} /></button>
                  </div>
                ))}
              </div>
            )}
          </form>

          <div className="admin-column">
            <PreviewPanel post={current} bodyText={bodyText} mediaBlocks={mediaBlocks} />
            <UsersPanel users={users} refresh={loadAdminData} />
          </div>
        </section> : (
          <VisualGalleryAdmin
            items={galleryItems}
            setItems={setGalleryItems}
            refresh={loadAdminData}
          />
        )}
      </section>
    </main>
  );
}

function VisualGalleryAdmin({ items, setItems, refresh }) {
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const labels = {
    main: "Glavna slika",
    top: "Zgornja desna slika",
    bottom: "Spodnja desna slika"
  };

  function updateItem(slot, changes) {
    setItems((current) => current.map((item) => (
      item.slot === slot ? { ...item, ...changes } : item
    )));
    setNotice("");
    setError("");
  }

  async function saveGallery(event) {
    event.preventDefault();
    setSaving(true);
    setNotice("");
    setError("");
    try {
      const data = await api.put("/api/admin/gallery", { items });
      setItems(data.items || items);
      setNotice("Galerija je shranjena in vidna vsem obiskovalcem.");
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="gallery-admin glass-panel">
      <form onSubmit={saveGallery}>
        <div className="editor-top">
          <div>
            <span className="section-kicker"><Sparkles size={14} /> Vizualna galerija</span>
            <h2>Uredi galerijo</h2>
            <p>Spremeni slike in napise. Položaj ter oblikovanje galerije ostaneta nespremenjena.</p>
          </div>
          <button className="primary-button" disabled={saving}>
            <Save size={17} /> {saving ? "Shranjujem…" : "Shrani galerijo"}
          </button>
        </div>
        {notice && <div className="notice success">{notice}</div>}
        {error && <div className="notice error">{error}</div>}
        <div className="gallery-admin-grid">
          {items.map((item) => (
            <article className="gallery-admin-card" key={item.slot}>
              <div className="gallery-admin-preview">
                <img src={item.imageUrl} alt={item.altText} />
                <span>{labels[item.slot]}</span>
              </div>
              <MediaUploader
                label="Zamenjaj sliko"
                accept="image/*"
                onUploaded={(media) => updateItem(item.slot, { imageUrl: media.urlPath })}
              />
              <label>
                Napis
                <input
                  value={item.caption}
                  maxLength="180"
                  onChange={(event) => updateItem(item.slot, { caption: event.target.value })}
                />
              </label>
              <label>
                Opis slike
                <textarea
                  rows="3"
                  value={item.altText}
                  maxLength="240"
                  onChange={(event) => updateItem(item.slot, { altText: event.target.value })}
                />
              </label>
            </article>
          ))}
        </div>
      </form>
    </section>
  );
}

function MediaUploader({ label, accept, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function upload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    const form = new FormData();
    form.append("file", file);
    try {
      const data = await api.post("/api/admin/media", form);
      onUploaded(data.media);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  return (
    <label className="upload-button">
      <Upload size={16} />
      <span>{busy ? "Nalaganje..." : label}</span>
      <input type="file" accept={accept} onChange={upload} />
      {error && <em>{error}</em>}
    </label>
  );
}

function TranscriptionBox({ onText }) {
  const [recording, setRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function transcribeBlob(blob, filename = "voice-note.webm") {
    setBusy(true);
    setError("");
    const form = new FormData();
    form.append("audio", blob, filename);
    try {
      const data = await api.post("/api/admin/transcribe", form);
      onText(data.text);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleRecording() {
    if (recording && recorder) {
      recorder.stop();
      setRecording(false);
      return;
    }

    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("Snemanje v brskalniku tukaj ni podprto. Namesto tega naloži zvočno datoteko.");
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const chunks = [];
    const nextRecorder = new MediaRecorder(stream);
    nextRecorder.ondataavailable = (event) => chunks.push(event.data);
    nextRecorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      transcribeBlob(new Blob(chunks, { type: "audio/webm" }));
    };
    nextRecorder.start();
    setRecorder(nextRecorder);
    setRecording(true);
  }

  return (
    <div className="transcription-box">
      <div>
        <strong><Mic size={16} /> Govor v besedilo</strong>
        <span>Posnemi ali naloži zvok in vstavi prepis v vsebino.</span>
      </div>
      <button type="button" className={recording ? "primary-button recording" : "outline-button"} onClick={toggleRecording} disabled={busy}>
        <Mic size={16} /> {recording ? "Ustavi snemanje" : "Snemaj"}
      </button>
      <label className="upload-button compact">
        <Upload size={16} />
        <span>{busy ? "Prepisujem..." : "Naloži zvok"}</span>
        <input type="file" accept="audio/*" onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) transcribeBlob(file, file.name);
          event.target.value = "";
        }} />
      </label>
      {error && <div className="notice error">{error}</div>}
    </div>
  );
}

function UsersPanel({ users, refresh }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function addUser(event) {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      await api.post("/api/admin/users", form);
      setForm({ name: "", email: "", password: "" });
      setNotice("Administrator dodan");
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleUser(user) {
    await api.patch(`/api/admin/users/${user.id}`, { active: !user.active });
    await refresh();
  }

  return (
    <section className="users-panel glass-panel">
      <h2><Users size={20} /> Administratorji</h2>
      {notice && <div className="notice success">{notice}</div>}
      {error && <div className="notice error">{error}</div>}
      <form onSubmit={addUser}>
        <input placeholder="Ime" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        <input placeholder="E-pošta" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        <input placeholder="Začasno geslo" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
        <button className="outline-button"><Plus size={16} /> Dodaj administratorja</button>
      </form>
      <div className="user-list">
        {users.map((admin) => (
          <div key={admin.id}>
            <span>{admin.name}<small>{admin.email}</small></span>
            <button className="ghost-button" onClick={() => toggleUser(admin)}>{admin.active ? "Deaktiviraj" : "Aktiviraj"}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

function PreviewPanel({ post, bodyText, mediaBlocks }) {
  const preview = {
    ...post,
    content: {
      blocks: [
        ...bodyTextToBlocks(bodyText),
        ...mediaBlocks
      ]
    }
  };

  return (
    <section className="preview glass-panel">
      <span className="section-kicker"><Video size={14} /> Predogled</span>
      <h2>{preview.title || "Objava brez naslova"}</h2>
      <p>{formatDate(preview.publicationDate ? `${preview.publicationDate}T12:00:00.000Z` : preview.publishedAt)}</p>
      <p>{preview.excerpt || "Tvoj povzetek bo prikazan tukaj."}</p>
      <PostCover post={preview} index={1} />
      <div className="preview-body">
        {preview.content.blocks.slice(0, 4).map((block, index) => <ContentBlock key={index} block={block} />)}
      </div>
    </section>
  );
}

function PostCard({ post, index, navigate }) {
  const categories = ["Mindset", "Sound", "Style", "Life"]; 
  return (
    <article className={`post-card post-card-${index % 4}`} onClick={() => navigate(`/journal/${post.slug}`)}>
      <PostCover post={post} index={index} />
      <div className="post-card-body">
        <span>{categories[index % categories.length]} · {formatDate(post.publishedAt || post.createdAt)}</span>
        <h3>{post.title}</h3>
        <p>{post.excerpt}</p>
        <button className="card-arrow" aria-label={`Read ${post.title}`}><ArrowRight size={18} /></button>
      </div>
    </article>
  );
}

function PostCover({ post, index = 0, large = false }) {
  if (post.coverUrl) {
    return (
      <div className={large ? "post-cover large" : "post-cover"}>
        {post.coverMimeType?.startsWith("video/") ? (
          <video src={post.coverUrl} controls />
        ) : (
          <img src={post.coverUrl} alt="" />
        )}
      </div>
    );
  }
  const fallbackCover = FALLBACK_COVERS[index % FALLBACK_COVERS.length];
  return (
    <div className={large ? "post-cover large" : "post-cover"}>
      <img src={fallbackCover} alt="" />
    </div>
  );
}

function parseInlineText(text) {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|_[^_]+_|\[[^\]]+\]\(https?:\/\/[^)]+\))/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));

    const token = match[0];
    if (token.startsWith("**")) {
      parts.push({ type: "strong", text: token.slice(2, -2) });
    } else if (token.startsWith("_")) {
      parts.push({ type: "em", text: token.slice(1, -1) });
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
      if (linkMatch) parts.push({ type: "link", text: linkMatch[1], href: linkMatch[2] });
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : [text];
}

function InlineText({ text }) {
  return text.split("\n").map((line, lineIndex, lines) => (
    <span key={`${line}-${lineIndex}`}>
      {parseInlineText(line).map((part, partIndex) => {
        if (typeof part === "string") return <span key={partIndex}>{part}</span>;
        if (part.type === "strong") return <strong key={partIndex}>{part.text}</strong>;
        if (part.type === "em") return <em key={partIndex}>{part.text}</em>;
        return <a key={partIndex} href={part.href} target="_blank" rel="noreferrer">{part.text}</a>;
      })}
      {lineIndex < lines.length - 1 && <br />}
    </span>
  ));
}

function ContentBlock({ block }) {
  if (block.type === "image") {
    return (
      <figure>
        <img src={block.url} alt={block.caption || ""} />
        {block.caption && <figcaption>{block.caption}</figcaption>}
      </figure>
    );
  }

  if (block.type === "video") {
    return (
      <figure>
        <video src={block.url} controls />
        {block.caption && <figcaption>{block.caption}</figcaption>}
      </figure>
    );
  }

  const text = String(block.text || "").trim();
  if (text.startsWith("## ")) return <h2 className="content-heading"><InlineText text={text.slice(3)} /></h2>;
  if (text.startsWith("> ")) return <blockquote><InlineText text={text.replace(/^> ?/gm, "")} /></blockquote>;

  const lines = text.split("\n").map((line) => line.trim()).filter(Boolean);
  if (lines.length > 0 && lines.every((line) => line.startsWith("- "))) {
    return (
      <ul className="content-list">
        {lines.map((line, index) => <li key={index}><InlineText text={line.slice(2)} /></li>)}
      </ul>
    );
  }

  return <p><InlineText text={text} /></p>;
}

function Waveform({ active = false, bars = 44 }) {
  return (
    <div className={active ? "waveform active" : "waveform"} aria-hidden="true">
      {Array.from({ length: bars }).map((_, index) => (
        <i key={index} style={{ "--h": `${20 + ((index * 17) % 46)}%`, "--d": `${index * 28}ms` }} />
      ))}
    </div>
  );
}

function Footer() {
  const { editable } = useContext(SiteTextContext);
  const text = (field) => editable(field);
  return (
    <footer className="footer">
      <div>
        <img className="footer-logo" src="/assets/lola-absolut-logo.png" alt="Lola Absolut" />
        <p>{text("footerTagline", LANDING_TEXT_DEFAULTS.footerTagline)}</p>
      </div>
      <div className="footer-center">
        <span>{text("footerStories", LANDING_TEXT_DEFAULTS.footerStories)}</span>
        <i />
        <span>{text("footerGallery", LANDING_TEXT_DEFAULTS.footerGallery)}</span>
        <i />
        <span>{text("footerContact", LANDING_TEXT_DEFAULTS.footerContact)}</span>
      </div>
      <div className="footer-socials">
        <a href="https://www.instagram.com/lola_absolut/" aria-label="Instagram"><Instagram size={18} /></a>
        <a href="mailto:hello@lolaabsolut.com" aria-label="E-pošta">@</a>
      </div>
    </footer>
  );
}

function NewsletterForm({ editable = null, editing = false }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const text = (field, fallback) => editable ? editable(field) : fallback;

  function submit(event) {
    event.preventDefault();
    if (editing) return;
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    setStatus(valid ? "success" : "error");
  }

  return (
    <section className="newsletter-section" id="contact">
      <div>
        <p className="eyebrow">{text("newsletterEyebrow", LANDING_TEXT_DEFAULTS.newsletterEyebrow)}</p>
        <h2>{text("newsletterTitleTop", LANDING_TEXT_DEFAULTS.newsletterTitleTop)}<br /><span>{text("newsletterTitleBottom", LANDING_TEXT_DEFAULTS.newsletterTitleBottom)}</span></h2>
        <p>{text("newsletterCopy", LANDING_TEXT_DEFAULTS.newsletterCopy)}</p>
      </div>
      <form onSubmit={submit} noValidate>
        <label htmlFor="newsletter-email">{text("newsletterEmailLabel", LANDING_TEXT_DEFAULTS.newsletterEmailLabel)}</label>
        <div className="newsletter-row">
          <input id="newsletter-email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); setStatus("idle"); }} aria-describedby="newsletter-note newsletter-status" aria-invalid={status === "error"} placeholder="YOU@SOMEWHERE.COM" />
          <button className="primary-button">{text("newsletterCta", LANDING_TEXT_DEFAULTS.newsletterCta)} <ArrowRight size={17} /></button>
        </div>
        <p id="newsletter-note">{text("newsletterLegal", LANDING_TEXT_DEFAULTS.newsletterLegal)}</p>
        <p id="newsletter-status" className={`newsletter-status ${status}`} role="status">
          {status === "error" && "Enter a valid email address."}
          {status === "success" && "You are on the list. Welcome to the next edition."}
        </p>
      </form>
    </section>
  );
}

function formatDate(value) {
  if (!value) return "Osnutek";
  return new Intl.DateTimeFormat("sl-SI", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Ljubljana" }).format(new Date(value));
}

function dateInputValue(value) {
  if (!value) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Europe/Ljubljana"
  }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function statusLabel(status) {
  return status === "published" ? "Objavljeno" : "Osnutek";
}

const rootElement = document.getElementById("root");
const root = window.__lolaabsolutRoot || createRoot(rootElement);
window.__lolaabsolutRoot = root;
root.render(<App />);
